import moment from "moment";
import { api } from "../api";
import { Storage } from "../storage";
import { groupByKey } from "../utils/array";
import { LocalData } from "./localData";
import { DownloadProgress, formatProgressNumber, formatSyncType, setDownloadProgress } from "./downloadProgress";
import { ServerError } from "../errors/ServerError";

const DOWNLOAD_REQUEST_TIMEOUT_MS = 120000;
const DOWNLOAD_MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;

interface Definition {
  apiKey: string;
  localData: InstanceType<typeof LocalData>;
  isGlobal: boolean;
}

interface ManifestType {
  apiKey: string;
  total: number;
}

interface ManifestResponse {
  snapshot: string;
  chunkSize: number;
  types: ManifestType[];
}

interface ChunkResponse {
  type: string;
  snapshot: string;
  data: any[];
  count: number;
  nextCursor: string | null;
  done: boolean;
}

interface DownloadCheckpoint {
  endpoint: string;
  updatedSince: string | null;
  snapshot: string;
  chunkSize: number;
  types: ManifestType[];
  currentTypeIndex: number;
  cursor: string | null;
  currentTypeDownloaded: number;
  recordsDownloaded: number;
  updatedAt: string;
}

export class ChunkedDownloader {

  private endpoint = ''
  private definitions: Definition[] = []

  public setEndpoint(endpoint: string) {
    this.endpoint = endpoint;
  }

  public addDefinition(definition: Definition) {
    this.definitions.push(definition)
  }

  public async getLastSuccessfulDownloadDate(): Promise<string> {
    const lastSuccessfulSyncDate = await Storage.getItem('last-successful-download-date');
    return lastSuccessfulSyncDate
  }

  private async updateLastSuccessfulDownloadDate(now: string): Promise<void> {
    await Storage.setItem('last-successful-download-date', now);
  }

  private getCheckpointKey() {
    return `chunked-download-checkpoint-${this.endpoint}`;
  }

  private async getResumeCheckpoint(updatedSince: string | null): Promise<DownloadCheckpoint | null> {
    const checkpoint = await Storage.getItem<DownloadCheckpoint>(this.getCheckpointKey());

    if (
      !checkpoint ||
      checkpoint.endpoint !== this.endpoint ||
      checkpoint.updatedSince !== updatedSince ||
      !checkpoint.snapshot ||
      !Array.isArray(checkpoint.types) ||
      !Number.isFinite(checkpoint.chunkSize) ||
      checkpoint.chunkSize <= 0
    ) {
      return null;
    }

    return checkpoint;
  }

  private async saveCheckpoint(checkpoint: Omit<DownloadCheckpoint, 'endpoint' | 'updatedAt'>): Promise<void> {
    await Storage.setItem(this.getCheckpointKey(), {
      ...checkpoint,
      endpoint: this.endpoint,
      updatedAt: new Date().toISOString(),
    });
  }

  private async clearCheckpoint(): Promise<void> {
    await Storage.setItem(this.getCheckpointKey(), null);
  }

  private getDefinition(apiKey: string): Definition | undefined {
    return this.definitions.find(definition => definition.apiKey === apiKey);
  }

  private async saveChunk(definition: Definition, definitionResults: any[]): Promise<void> {
    if (definition.isGlobal) {
      await definition.localData.upsert(definitionResults, false);
      return;
    }

    const groupedByLocationId = groupByKey<any>(definitionResults, 'locationId')
    for (const [locationId, values] of Object.entries(groupedByLocationId)) {
      await definition.localData.upsert(values, false, locationId);
    }
  }

  private setProgress(progress: Partial<DownloadProgress>) {
    setDownloadProgress(progress);
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private getErrorMessage(error: any): string {
    if (!error) {
      return 'Unknown error';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  }

  private async requestWithRetries<T>(description: string, request: () => Promise<T>): Promise<T> {
    let lastError: any;

    for (let attempt = 1; attempt <= DOWNLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        return await request();
      } catch (error: any) {
        lastError = error;

        if (error instanceof ServerError && error.status && error.status < 500) {
          throw error;
        }

        if (attempt === DOWNLOAD_MAX_ATTEMPTS) {
          break;
        }

        await this.sleep(RETRY_DELAY_MS * attempt);
      }
    }

    throw new Error(`${description} failed after ${DOWNLOAD_MAX_ATTEMPTS} attempts: ${this.getErrorMessage(lastError)}`);
  }

  private getCurrentProgressErrorPrefix(type?: ManifestType, currentTypeDownloaded = 0): string {
    if (!type) {
      return 'Download failed';
    }

    return `Download failed while downloading ${formatSyncType(type.apiKey)} (${formatProgressNumber(currentTypeDownloaded)} / ${formatProgressNumber(type.total)})`;
  }

  public async download(): Promise<void> {
    if (!this.endpoint) {
      return;
    }

    const updatedSince = await this.getLastSuccessfulDownloadDate();
    const updatedSinceWithBuffer = updatedSince ? moment(updatedSince).subtract(10, 'minutes').toISOString() : null

    this.setProgress({
      status: 'starting',
      currentType: undefined,
      currentTypeIndex: 0,
      totalTypes: 0,
      currentTypeDownloaded: 0,
      currentTypeTotal: 0,
      recordsDownloaded: 0,
      totalRecords: 0,
      isResuming: false,
      error: undefined,
    });

    let errorPrefix = 'Download failed';

    try {
      const resumeCheckpoint = await this.getResumeCheckpoint(updatedSinceWithBuffer);
      let manifest: ManifestResponse;
      let startTypeIndex = 0;
      let resumeCursor: string | null = null;
      let resumeCurrentTypeDownloaded = 0;
      let recordsDownloaded = 0;
      const isResuming = Boolean(resumeCheckpoint);

      if (resumeCheckpoint) {
        manifest = {
          snapshot: resumeCheckpoint.snapshot,
          chunkSize: resumeCheckpoint.chunkSize,
          types: resumeCheckpoint.types,
        };
        startTypeIndex = resumeCheckpoint.currentTypeIndex;
        resumeCursor = resumeCheckpoint.cursor;
        resumeCurrentTypeDownloaded = resumeCheckpoint.currentTypeDownloaded;
        recordsDownloaded = resumeCheckpoint.recordsDownloaded;

        this.setProgress({
          status: 'starting',
          currentType: undefined,
          currentTypeIndex: startTypeIndex,
          totalTypes: resumeCheckpoint.types.length,
          currentTypeDownloaded: resumeCurrentTypeDownloaded,
          currentTypeTotal: 0,
          recordsDownloaded,
          totalRecords: resumeCheckpoint.types.reduce((total, type) => total + type.total, 0),
          isResuming: true,
          error: undefined,
        });
      } else {
        const manifestResults = await this.requestWithRetries('Sync manifest request', () => api.get(`${this.endpoint}/manifest`, {
          params: { updatedSince: updatedSinceWithBuffer },
          timeout: DOWNLOAD_REQUEST_TIMEOUT_MS,
        }));
        manifest = manifestResults.data as ManifestResponse;
      }

      const types = manifest.types.filter(type => this.getDefinition(type.apiKey));
      const totalRecords = types.reduce((total, type) => total + type.total, 0);
      startTypeIndex = Math.max(0, Math.min(startTypeIndex, types.length));
      recordsDownloaded = Math.max(0, Math.min(recordsDownloaded, totalRecords));

      if (!resumeCheckpoint) {
        await this.saveCheckpoint({
          updatedSince: updatedSinceWithBuffer,
          snapshot: manifest.snapshot,
          chunkSize: manifest.chunkSize,
          types,
          currentTypeIndex: 0,
          cursor: null,
          currentTypeDownloaded: 0,
          recordsDownloaded: 0,
        });
      }

      for (let index = startTypeIndex; index < types.length; index++) {
        const type = types[index];
        const definition = this.getDefinition(type.apiKey);
        let cursor: string | null = isResuming && index === startTypeIndex ? resumeCursor : null;
        let currentTypeDownloaded = isResuming && index === startTypeIndex ? resumeCurrentTypeDownloaded : 0;
        const seenCursors: { [cursor: string]: boolean } = {};

        if (!definition) {
          continue;
        }

        if (type.total === 0) {
          await this.saveCheckpoint({
            updatedSince: updatedSinceWithBuffer,
            snapshot: manifest.snapshot,
            chunkSize: manifest.chunkSize,
            types,
            currentTypeIndex: index + 1,
            cursor: null,
            currentTypeDownloaded: 0,
            recordsDownloaded,
          });
          continue;
        }

        if (cursor) {
          seenCursors[cursor] = true;
        }

        errorPrefix = this.getCurrentProgressErrorPrefix(type, currentTypeDownloaded);

        this.setProgress({
          status: 'downloading',
          currentType: type.apiKey,
          currentTypeIndex: index + 1,
          totalTypes: types.length,
          currentTypeDownloaded,
          currentTypeTotal: type.total,
          recordsDownloaded,
          totalRecords,
          isResuming,
        });

        do {
          const chunkResults = await this.requestWithRetries(`Sync chunk request for ${formatSyncType(type.apiKey)}`, () => api.get(`${this.endpoint}/chunk`, {
              params: {
                type: type.apiKey,
                updatedSince: updatedSinceWithBuffer,
                snapshot: manifest.snapshot,
                cursor,
                limit: manifest.chunkSize,
              },
              timeout: DOWNLOAD_REQUEST_TIMEOUT_MS,
            }));
          const chunk = chunkResults.data as ChunkResponse;
          const data = chunk.data || [];

          await this.saveChunk(definition, data);

          if (chunk.nextCursor && chunk.nextCursor === cursor) {
            throw new Error(`${formatSyncType(type.apiKey)} download stopped because the server returned the same cursor twice`);
          }

          if (chunk.nextCursor && seenCursors[chunk.nextCursor]) {
            throw new Error(`${formatSyncType(type.apiKey)} download stopped because the server repeated a previous cursor`);
          }

          if (chunk.nextCursor) {
            seenCursors[chunk.nextCursor] = true;
          }

          cursor = chunk.nextCursor;
          currentTypeDownloaded += data.length;
          recordsDownloaded += data.length;
          errorPrefix = this.getCurrentProgressErrorPrefix(type, currentTypeDownloaded);

          await this.saveCheckpoint({
            updatedSince: updatedSinceWithBuffer,
            snapshot: manifest.snapshot,
            chunkSize: manifest.chunkSize,
            types,
            currentTypeIndex: cursor ? index : index + 1,
            cursor,
            currentTypeDownloaded: cursor ? currentTypeDownloaded : 0,
            recordsDownloaded,
          });

          this.setProgress({
            status: 'downloading',
            currentType: type.apiKey,
            currentTypeIndex: index + 1,
            totalTypes: types.length,
            currentTypeDownloaded,
            currentTypeTotal: type.total,
            recordsDownloaded,
            totalRecords,
            isResuming,
          });
        } while (cursor);
      }

      await this.updateLastSuccessfulDownloadDate(manifest.snapshot)
      await this.clearCheckpoint()

      this.setProgress({
        status: 'complete',
        currentType: undefined,
        currentTypeIndex: types.length,
        totalTypes: types.length,
        currentTypeDownloaded: 0,
        currentTypeTotal: 0,
        recordsDownloaded,
        totalRecords,
        isResuming: false,
      });
    } catch(error: any) {
      if (error instanceof ServerError && error.status === 404) {
        await this.clearCheckpoint();
        throw error;
      }

      this.setProgress({
        status: 'error',
        error: `${errorPrefix}: ${this.getErrorMessage(error)}`,
      });
      throw error;
    }
  }

}
